import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css'; // Import SunEditor CSS
import { Save, Plus, FileText, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

const ContractEditor = () => {
  const [contracts, setContracts] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState(null);
  const [contractName, setContractName] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchContracts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api2/admin/contracts/content');
      setContracts(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching contracts:', err);
      setError('Failed to fetch contracts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  const loadContract = (id) => {
    const contract = contracts.find(c => c.contract_content_id === id);
    if (contract) {
      setSelectedContractId(contract.contract_content_id);
      setContractName(contract.contract_name);
      setContentHtml(contract.content_html || '');
      setDescription(contract.description || '');
      setIsActive(contract.is_active);
      setError(null);
      setMessage(null);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError(null);
    setMessage(null);

    if (!contractName || !contentHtml) {
      setError('Contract name and HTML content are required.');
      setIsLoading(false);
      return;
    }

    const contractData = {
      contract_name: contractName,
      content_html: contentHtml,
      description: description,
      is_active: isActive
    };

    try {
      if (selectedContractId) {
        // Update existing contract
        await axios.put(`/api2/admin/contracts/content/${selectedContractId}`, contractData);
        setMessage('Contract updated successfully!');
      } else {
        // Create new contract
        const response = await axios.post('/api2/admin/contracts/content', contractData);
        setMessage('Contract created successfully!');
        setSelectedContractId(response.data.contract_content_id); // Select the newly created contract
      }
      fetchContracts(); // Refresh the list
    } catch (err) {
      console.error('Error saving contract:', err);
      setError('Failed to save contract. ' + (err.response?.data?.message || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewContract = () => {
    setSelectedContractId(null);
    setContractName('');
    setContentHtml('');
    setDescription('');
    setIsActive(true);
    setError(null);
    setMessage(null);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      padding: '32px 0',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    wrapper: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 16px'
    },
    header: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb',
      marginBottom: '32px'
    },
    headerContent: {
      padding: '16px 24px',
      borderBottom: '1px solid #e5e7eb'
    },
    headerTop: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#111827',
      margin: 0
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    button: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '8px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      textDecoration: 'none'
    },
    buttonSecondary: {
      color: '#374151',
      backgroundColor: 'white',
      ':hover': {
        backgroundColor: '#f9fafb'
      }
    },
    buttonPrimary: {
      color: 'white',
      backgroundColor: '#2563eb',
      borderColor: 'transparent',
      ':hover': {
        backgroundColor: '#1d4ed8'
      }
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    statusMessages: {
      padding: '16px 24px',
      borderBottom: '1px solid #e5e7eb'
    },
    alert: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px',
      borderRadius: '6px',
      border: '1px solid'
    },
    alertError: {
      backgroundColor: '#fef2f2',
      borderColor: '#fecaca',
      color: '#dc2626'
    },
    alertSuccess: {
      backgroundColor: '#f0fdf4',
      borderColor: '#bbf7d0',
      color: '#16a34a'
    },
    mainGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 3fr',
      gap: '32px'
    },
    sidebar: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb'
    },
    sidebarHeader: {
      padding: '12px 16px',
      borderBottom: '1px solid #e5e7eb'
    },
    sidebarTitle: {
      fontSize: '18px',
      fontWeight: '500',
      color: '#111827',
      margin: 0
    },
    sidebarContent: {
      padding: '16px'
    },
    select: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      outline: 'none',
      ':focus': {
        borderColor: '#2563eb',
        boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)'
      }
    },
    contractList: {
      marginTop: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    contractCard: {
      padding: '12px',
      borderRadius: '6px',
      border: '1px solid #e5e7eb',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    contractCardSelected: {
      backgroundColor: '#eff6ff',
      borderColor: '#bfdbfe',
      color: '#1e40af'
    },
    contractCardHover: {
      backgroundColor: '#f9fafb'
    },
    contractCardTop: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    contractName: {
      fontSize: '14px',
      fontWeight: '500',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: '500'
    },
    statusActive: {
      backgroundColor: '#dcfce7',
      color: '#166534'
    },
    statusInactive: {
      backgroundColor: '#f3f4f6',
      color: '#374151'
    },
    contractDescription: {
      fontSize: '12px',
      color: '#6b7280',
      marginTop: '4px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    mainContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb'
    },
    cardHeader: {
      padding: '16px 24px',
      borderBottom: '1px solid #e5e7eb'
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: '500',
      color: '#111827',
      margin: 0
    },
    cardContent: {
      padding: '24px'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '24px',
      marginBottom: '24px'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column'
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      outline: 'none',
      ':focus': {
        borderColor: '#2563eb',
        boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)'
      }
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    checkboxInput: {
      width: '16px',
      height: '16px',
      accentColor: '#2563eb'
    },
    textarea: {
      width: '100%',
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      fontFamily: 'monospace',
      outline: 'none',
      resize: 'vertical',
      ':focus': {
        borderColor: '#2563eb',
        boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)'
      }
    },
    helpText: {
      fontSize: '14px',
      color: '#6b7280',
      marginTop: '8px'
    },
    preview: {
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      padding: '16px',
      backgroundColor: '#f9fafb'
    },
    iconBlue: {
      color: '#2563eb'
    },
    iconRed: {
      color: '#dc2626'
    },
    iconGreen: {
      color: '#16a34a'
    }
  };

  return (
    <div className='content'>
    <div style={styles.container}>
      <div style={styles.wrapper}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.headerTop}>
              <div style={styles.headerLeft}>
                <FileText size={32} style={styles.iconBlue} />
                <h1 style={styles.title}>Contract Editor</h1>
              </div>
              <div style={styles.headerRight}>
                <button
                  onClick={handleNewContract}
                  style={{...styles.button, ...styles.buttonSecondary}}
                >
                  <Plus size={16} style={{marginRight: '8px'}} />
                  New Contract
                </button>
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  style={{
                    ...styles.button, 
                    ...styles.buttonPrimary,
                    ...(isLoading ? styles.buttonDisabled : {})
                  }}
                >
                  {isLoading ? (
                    <Loader2 size={16} style={{marginRight: '8px', animation: 'spin 1s linear infinite'}} />
                  ) : (
                    <Save size={16} style={{marginRight: '8px'}} />
                  )}
                  {selectedContractId ? 'Update Contract' : 'Create Contract'}
                </button>
              </div>
            </div>
          </div>

          {/* Status Messages */}
          {(error || message) && (
            <div style={styles.statusMessages}>
              {error && (
                <div style={{...styles.alert, ...styles.alertError}}>
                  <AlertCircle size={20} style={{marginRight: '12px', ...styles.iconRed}} />
                  <span>{error}</span>
                </div>
              )}
              {message && (
                <div style={{...styles.alert, ...styles.alertSuccess}}>
                  <CheckCircle size={20} style={{marginRight: '12px', ...styles.iconGreen}} />
                  <span>{message}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={styles.mainGrid}>
          {/* Sidebar - Contract List */}
          <div style={styles.sidebar}>
            <div style={styles.sidebarHeader}>
              <h3 style={styles.sidebarTitle}>Contracts</h3>
            </div>
            <div style={styles.sidebarContent}>
              <select
                onChange={(e) => loadContract(parseInt(e.target.value))}
                value={selectedContractId || ''}
                style={styles.select}
              >
                <option value="">-- Select Contract --</option>
                {contracts.map(contract => (
                  <option key={contract.contract_content_id} value={contract.contract_content_id}>
                    {contract.contract_name}
                  </option>
                ))}
              </select>
              
              {contracts.length > 0 && (
                <div style={styles.contractList}>
                  {contracts.map(contract => (
                    <div
                      key={contract.contract_content_id}
                      onClick={() => loadContract(contract.contract_content_id)}
                      style={{
                        ...styles.contractCard,
                        ...(selectedContractId === contract.contract_content_id ? styles.contractCardSelected : {})
                      }}
                    >
                      <div style={styles.contractCardTop}>
                        <span style={styles.contractName}>{contract.contract_name}</span>
                        <span style={{
                          ...styles.statusBadge,
                          ...(contract.is_active ? styles.statusActive : styles.statusInactive)
                        }}>
                          {contract.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {contract.description && (
                        <p style={styles.contractDescription}>{contract.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div style={styles.mainContent}>
            {/* Contract Details Form */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>Contract Details</h3>
              </div>
              <div style={styles.cardContent}>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label htmlFor="contract-name" style={styles.label}>
                      Contract Name *
                    </label>
                    <input
                      type="text"
                      id="contract-name"
                      value={contractName}
                      onChange={(e) => setContractName(e.target.value)}
                      style={styles.input}
                      placeholder="Enter contract name"
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label htmlFor="description" style={styles.label}>
                      Description
                    </label>
                    <input
                      type="text"
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      style={styles.input}
                      placeholder="Enter description"
                    />
                  </div>
                </div>

                <div style={styles.checkbox}>
                  <input
                    type="checkbox"
                    id="is-active"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    style={styles.checkboxInput}
                  />
                  <label htmlFor="is-active" style={styles.label}>
                    Contract is active
                  </label>
                </div>
              </div>
            </div>

            {/* Rich Text Editor */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>Contract Content</h3>
              </div>
              <div style={styles.cardContent}>
                <div style={styles.formGroup}>
                  <label htmlFor="content-html" style={styles.label}>
                    Contract Content *
                  </label>
                  <div style={{ border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden' }}>
                    <SunEditor
                      setContents={contentHtml}
                      onChange={setContentHtml}
                      setOptions={{
                        height: 400,
                        buttonList: [
                          ['undo', 'redo'],
                          ['font', 'fontSize', 'formatBlock'],
                          ['paragraphStyle', 'blockquote'],
                          ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
                          ['fontColor', 'hiliteColor'],
                          ['removeFormat'],
                          ['outdent', 'indent'],
                          ['align', 'horizontalRule', 'list', 'lineHeight'],
                          ['table', 'link', 'image', 'video'],
                          ['fullScreen', 'showBlocks', 'codeView'],
                          ['preview', 'print'],
                          ['save', 'template'],
                        ],
                        font: ['Arial', 'Helvetica', 'Georgia', 'Times New Roman', 'Verdana'],
                        fontSize: [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72],
                        formats: ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
                        colorList: [
                          ['#000000', '#2C2C2C', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#EFEFEF', '#F3F3F3', '#FFFFFF'],
                          ['#980000', '#FF0000', '#FF9900', '#FFFF00', '#00FF00', '#00FFFF', '#4A86E8', '#0000FF', '#9900FF', '#FF00FF'],
                          ['#E6B8AF', '#F4CCCC', '#FCE5CD', '#FFF2CC', '#D9EAD3', '#D0E0E3', '#C9DAF8', '#CFE2F3', '#D9D2E9', '#EAD1DC'],
                          ['#DD7E6B', '#EA9999', '#F9CB9C', '#FFE599', '#B6D7A8', '#A2C4C9', '#A4C2F4', '#9FC5E8', '#B4A7D6', '#D5A6BD'],
                          ['#CC4125', '#E06666', '#F6B26B', '#FFD966', '#93C47D', '#76A5AF', '#6D9EEB', '#6FA8DC', '#8E7CC3', '#C27BA0'],
                          ['#A61C00', '#CC0000', '#E69138', '#F1C232', '#6AA84F', '#45818E', '#3C78D8', '#3D85C6', '#674EA7', '#A64D79'],
                          ['#85200C', '#990000', '#B45F06', '#BF9000', '#38761D', '#134F5C', '#1155CC', '#0B5394', '#351C75', '#741B47'],
                          ['#5B0F00', '#660000', '#783F04', '#7F6000', '#274E13', '#0C343D', '#1C4587', '#073763', '#20124D', '#4C1130']
                        ]
                      }}
                      placeholder="Enter your contract content here..."
                    />
                  </div>
                  <p style={styles.helpText}>
                    Use the rich text editor to create your contract content. The editor supports formatting, tables, links, and more.
                  </p>
                </div>
              </div>
            </div>

            {/* Preview Section */}
            {contentHtml && (
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>Preview</h3>
                </div>
                <div style={styles.cardContent}>
                  <div 
                    style={styles.preview}
                    dangerouslySetInnerHTML={{ __html: contentHtml }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        button:hover {
          transform: translateY(-1px);
        }
        
        .contract-card:hover {
          background-color: #f9fafb !important;
        }
        
        input:focus, textarea:focus, select:focus {
          border-color: #2563eb !important;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1) !important;
        }
      `}</style>
    </div>
    </div>
  );
};

export default ContractEditor;